import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
    Body1,
    Body1Strong,
    Button,
    Divider,
    Field,
    Input,
    MessageBar,
    MessageBarBody,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Spinner,
} from "@fluentui/react-components";
import { Edit12Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

// component functions 
import * as ComponentFunctions from './ConsumptionAmount.functions';

// styles
import { CurrencySelectorComponent } from "../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent";
import { useStyles } from "./ConsumptionAmount.Styles";
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';

const ConsumptionAmountComponent = (props: ComponentFunctions.Props) => {

    const [t] = useTranslation();
    const styles = useStyles();
    const amountInputRef = useRef<HTMLInputElement>(null);
    const [newValueInput, setNewValueInput] = useState<string>((props.Consumption?.unitAmount ?? 0).toString());
    const [selectedCurrency, setSelectedCurrency] = useState<string>(props.Consumption?.currencyCode || 'ARS');
    const [localValidationError, setLocalValidationError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (props.Consumption?.unitAmount !== undefined) {
            setNewValueInput(props.Consumption.unitAmount.toString());
        }
        if (props.Consumption?.currencyCode) {
            setSelectedCurrency(props.Consumption.currencyCode);
        }
    }, [props.Consumption?.unitAmount, props.Consumption?.currencyCode]);

    useEffect(() => {
        if (props.successChangingAmount) {
            setOpen(false);
            setLocalValidationError(null);
        }
    }, [props.successChangingAmount]);

    useEffect(() => {
        if (open) {
            const focusFrame = globalThis.requestAnimationFrame(() => amountInputRef.current?.focus());
            return () => globalThis.cancelAnimationFrame(focusFrame);
        }
    }, [open]);

    const serverErrorMessage = useMemo(() => {
        return props.errorOnChangingAmount?.ErrorMessage || t('consumptionAmountEditor.genericSaveError');
    }, [props.errorOnChangingAmount, t]);

    const parseAmount = useCallback((): number | null => {
        const normalizedValue = newValueInput.trim().replace(',', '.');

        if (!normalizedValue.length) {
            return null;
        }

        const parsedValue = Number(normalizedValue);

        if (!Number.isFinite(parsedValue)) {
            return null;
        }

        return parsedValue;
    }, [newValueInput]);

    const handleSave = useCallback(() => {
        const parsedAmount = parseAmount();

        if (parsedAmount === null) {
            setLocalValidationError(t('consumptionAmountEditor.validation.invalidAmount'));
            return;
        }

        setLocalValidationError(null);
        props.ChangeAmount(props.ProtocolId, props.ProtocolConsumptionId, parsedAmount, selectedCurrency);
    }, [parseAmount, props, selectedCurrency, t]);

    const hasInvalidInput = newValueInput.trim().length > 0 && parseAmount() === null;

    const saveDisabled = props.isChangingAmount || newValueInput.trim().length === 0 || hasInvalidInput;

    const currentAmount = props.Consumption?.unitAmount == null
        ? '—'
        : formatCurrencyAmountWithIssue(props.Consumption.unitAmount, props.Consumption.currencyCode);

    return (
        <div className={styles.wrapper}>
            {props.showCurrentValue !== false && <Body1Strong className={styles.value}>{currentAmount}</Body1Strong>}
            <Popover
                withArrow
                open={open}
                onOpenChange={(_, data) => {
                    setOpen(data.open);
                    if (!data.open) {
                        setLocalValidationError(null);
                    }
                }}
            >
                <PopoverTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        size="small"
                        className={styles.triggerButton}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        aria-label={t('consumptionAmountEditor.openEditorAria')}
                        icon={<Edit12Regular />}
                    >
                        {t('consumptionAmountEditor.editAction')}
                    </Button>
                </PopoverTrigger>
                <PopoverSurface className={styles.popoverSurface} role="dialog" aria-label={t('consumptionAmountEditor.title')}>
                    <div className={styles.popoverContent}>
                        <Body1Strong>{t('consumptionAmountEditor.title')}</Body1Strong>
                        {localValidationError && (
                            <MessageBar intent="warning" role="alert">
                                <MessageBarBody>{localValidationError}</MessageBarBody>
                            </MessageBar>
                        )}
                        {props.failChangingAmount && (
                            <MessageBar intent="error" role="alert">
                                <MessageBarBody>{serverErrorMessage}</MessageBarBody>
                            </MessageBar>
                        )}
                        {props.isChangingAmount && (
                            <div className={styles.loadingStatus} role="status" aria-live="polite">
                                <Spinner size="extra-tiny" />
                                <Body1>{t('consumptionAmountEditor.saving')}</Body1>
                            </div>
                        )}
                        <Field
                            label={t('consumptionAmountEditor.amountLabel')}
                            validationState={hasInvalidInput ? 'error' : undefined}
                        >
                            <Input
                                ref={amountInputRef}
                                step={0.01}
                                type="number"
                                value={newValueInput}
                                aria-label={t('consumptionAmountEditor.amountInputAria')}
                                onChange={(e) => {
                                    setNewValueInput(e.target.value);
                                    if (localValidationError) {
                                        setLocalValidationError(null);
                                    }
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        handleSave();
                                    }

                                    if (event.key === 'Escape') {
                                        event.preventDefault();
                                        setOpen(false);
                                        setLocalValidationError(null);
                                    }
                                }}
                            />
                        </Field>
                        <Field label={t('consumptionAmountEditor.currencyLabel')}>
                            <CurrencySelectorComponent
                                errorMessage={undefined}
                                defaultSelected={selectedCurrency}
                                label={undefined}
                                onCurrencyChange={(currency: string) => setSelectedCurrency(currency)}
                            />
                        </Field>
                        <Divider />
                        <div className={styles.footer}>
                            <Button
                                appearance="primary"
                                size="small"
                                disabled={saveDisabled}
                                onClick={handleSave}
                                icon={props.isChangingAmount ? <Spinner size="extra-tiny" /> : undefined}
                            >
                                {props.isChangingAmount ? t('consumptionAmountEditor.saving') : t('consumptionAmountEditor.save')}
                            </Button>
                        </div>
                    </div>
                </PopoverSurface>
            </Popover>
        </div>
    );
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionAmountComponent as any);